import { useMemo } from 'react';

import { useConfig } from '../contexts/config';

const buildPaths = (rootURL: string) => ({
  dashboardPath: () => rootURL,
  createLoginPath: () => `${rootURL}/create`,
  statisticsPath: () => `${rootURL}/analytics`,
  changeMethodPath: (projectId: string) => `${rootURL}/${projectId}/change-method`,
  widgetEditorPath: (projectId: string) => `${rootURL}/${projectId}/customization/widget-editor`,
  navigationPath: (projectId: string) => `${rootURL}/${projectId}/navigation`,
  babkaLoginPath: (clientId: number) => `${rootURL}/babka-login-client/${clientId}`,
  callbackUrlsSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/callback-urls`,
  signUpSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/sign-up`,
  emailSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/email`,
  passwordSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/password`,
  usernameSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/username`,
  passwordlessTypePath: (projectId: string) => `${rootURL}/${projectId}/settings/passwordless-type`,
  apiSettingsPath: (projectId: string) => `${rootURL}/${projectId}/settings/api`,
  oauthPath: (projectId: string) => `${rootURL}/${projectId}/security/oauth2`,
  signaturePath: (projectId: string) => `${rootURL}/${projectId}/security/signature`,
  mfaPath: (projectId: string) => `${rootURL}/${projectId}/security/mfa`,
  socialConnectionPath: (projectId: string) =>
    `${rootURL}/${projectId}/authentication/social-connection`,
  socialConnectionSettingsPath: (projectId: string, name: string) =>
    `${rootURL}/${projectId}/authentication/social-connection/${name}`,
  platformSettingsPath: (projectId: string) =>
    `${rootURL}/${projectId}/authentication/platform-settings`,
  enterpriseConnectionsPath: (projectId: string) =>
    `${rootURL}/${projectId}/authentication/enterprise-connections`,
  passkeyPath: (projectId: string) => `${rootURL}/${projectId}/authentication/passkey`,
  miscellaneousPath: (projectId: string) => `${rootURL}/${projectId}/authentication/miscellaneous`,
  usersPath: (projectId: string) => `${rootURL}/${projectId}/user-database/users`,
  userSettingPath: (projectId: string, userId: string) =>
    `${rootURL}/${projectId}/user-database/user/${userId}/settings`,
  userDataStoragePath: (projectId: string) => `${rootURL}/${projectId}/user-database/storage`,
  userSupplementaryInfoPath: (projectId: string) =>
    `${rootURL}/${projectId}/user-database/supplementary`,
  userAttributesPath: (projectId: string) => `${rootURL}/${projectId}/user-database/attributes`,
  emailCustomProviderPath: (projectId: string) => `${rootURL}/${projectId}/providers/email`,
  smsCustomProviderPath: (projectId: string) => `${rootURL}/${projectId}/providers/sms`,
  customizeWidgetPath: (projectId: string) => `${rootURL}/${projectId}/customization/widget`,
  emailCustomizationPath: (projectId: string) => `${rootURL}/${projectId}/customization/email`,
  smsCustomizationPath: (projectId: string) => `${rootURL}/${projectId}/customization/sms`,
  termsAndPoliciesLegalSettingsPath: (projectId: string) =>
    `${rootURL}/${projectId}/legal-settings/terms-and-policies`,
  ageRestrictionsPath: (projectId: string) =>
    `${rootURL}/${projectId}/legal-settings/age-restrictions`,
  launcherPath: () => rootURL.replace(/(new-login|login)$/, 'launchers'),
  launcherProjectPath: (projectId: string) =>
    `${rootURL.replace(/(new-login|login)$/, 'launchers')}/${projectId}`,
  launcherProjectPathNew: (id: number) =>
    `${rootURL.replace(/(new-login|login)$/, 'new-launchers')}/${id}`
});

export const usePaths = (): ReturnType<typeof buildPaths> => {
  const { rootURL = '' } = useConfig();
  return useMemo(() => buildPaths(rootURL), [rootURL]);
};

export const agreementPath = (merchantId: number): string => `/${merchantId}/agreement/product`;
export const analyticsPath = (merchantId: number): string =>
  `/${merchantId}/analytics/products/login`;
